import React, { useEffect, useMemo, useState } from "react";
import {
  ProviderManifest,
  ProviderFieldDefinition,
} from "../../services/api";
import {
  StrategyConfigDraft,
  STTLLMTTSStrategyConfigDraft,
  LLMTTSStrategyConfigDraft,
  Voice2VoiceStrategyConfigDraft,
  DEFAULT_STT_LLM_TTS_CONFIG,
  DEFAULT_LLM_TTS_CONFIG,
  DEFAULT_VOICE2VOICE_CONFIG,
  cloneStrategyConfig,
  ProviderSettingsDraft,
} from "../../types/strategyConfig";
import { Strategy, Provider, StrategyProviders } from "../../types/strategies";

type ProviderServiceKey = "stt" | "llm" | "tts";

interface StrategySelectorProps {
  strategy: Strategy | null;
  strategyProviders: StrategyProviders;
  strategyConfig: StrategyConfigDraft;
  manifest: ProviderManifest | null;
  onStrategyChange: (strategy: Strategy) => void;
  onStrategyProvidersChange: (providers: StrategyProviders) => void;
  onStrategyConfigChange: (config: StrategyConfigDraft) => void;
}

const serviceLabels: Record<ProviderServiceKey, string> = {
  stt: "Speech-to-Text",
  llm: "Language Model",
  tts: "Text-to-Speech",
};

type ServiceProviderDefinition = ProviderManifest[ProviderServiceKey][number];

const StrategySelector: React.FC<StrategySelectorProps> = ({
  strategy,
  strategyProviders,
  strategyConfig,
  manifest,
  onStrategyChange,
  onStrategyProvidersChange,
  onStrategyConfigChange,
}) => {
  const [expandedPanels, setExpandedPanels] = useState<Record<ProviderServiceKey, boolean>>({
    stt: true,
    llm: true,
    tts: false,
  });
  const [numberDraftValues, setNumberDraftValues] = useState<Record<string, string>>(
    {}
  );
  const [idleTimeoutInput, setIdleTimeoutInput] = useState<string>("");

  const isSttConfig = (
    config: StrategyConfigDraft
  ): config is STTLLMTTSStrategyConfigDraft =>
    Boolean(
      config &&
        typeof (config as STTLLMTTSStrategyConfigDraft).user_idle_timeout === "number"
    );

  const isLlmTtsConfig = (
    config: StrategyConfigDraft
  ): config is LLMTTSStrategyConfigDraft => {
    if (!config) return false;
    const candidate = config as unknown as Record<string, unknown>;
    return "llm" in candidate && "tts" in candidate && !("stt" in candidate);
  };

  const sttConfig = useMemo<STTLLMTTSStrategyConfigDraft | null>(() => {
    if (strategy !== Strategy.STT_LLM_TTS) {
      return null;
    }
    if (isSttConfig(strategyConfig)) {
      return strategyConfig;
    }
    return cloneStrategyConfig(DEFAULT_STT_LLM_TTS_CONFIG);
  }, [strategy, strategyConfig]);

  useEffect(() => {
    if (!sttConfig) {
      setIdleTimeoutInput("");
      return;
    }
    setIdleTimeoutInput(String(sttConfig.user_idle_timeout ?? 10));
  }, [sttConfig?.user_idle_timeout, sttConfig]);

  const llmTtsConfig = useMemo<LLMTTSStrategyConfigDraft | null>(() => {
    if (strategy !== Strategy.LLM_TTS) {
      return null;
    }
    if (isLlmTtsConfig(strategyConfig)) {
      return strategyConfig;
    }
    return cloneStrategyConfig(DEFAULT_LLM_TTS_CONFIG);
  }, [strategy, strategyConfig]);

  const isVoiceConfig = (
    config: StrategyConfigDraft
  ): config is Voice2VoiceStrategyConfigDraft =>
    Boolean(config && typeof (config as Voice2VoiceStrategyConfigDraft).model === "string");

  const activeServices = useMemo<ProviderServiceKey[]>(() => {
    if (strategy === Strategy.STT_LLM_TTS) {
      return ["stt", "llm", "tts"];
    }
    if (strategy === Strategy.LLM_TTS) {
      return ["llm", "tts"];
    }
    return [];
  }, [strategy]);

  const updateSttConfig = (mutator: (draft: STTLLMTTSStrategyConfigDraft) => void) => {
    if (strategy !== Strategy.STT_LLM_TTS) return;
    const base = sttConfig
      ? cloneStrategyConfig(sttConfig)
      : cloneStrategyConfig(DEFAULT_STT_LLM_TTS_CONFIG);
    mutator(base);
    onStrategyConfigChange(base);
  };

  const updateLlmTtsConfig = (mutator: (draft: LLMTTSStrategyConfigDraft) => void) => {
    if (strategy !== Strategy.LLM_TTS) return;
    const base = llmTtsConfig
      ? cloneStrategyConfig(llmTtsConfig)
      : cloneStrategyConfig(DEFAULT_LLM_TTS_CONFIG);
    mutator(base);
    onStrategyConfigChange(base);
  };

  const voice2VoiceConfig = useMemo<Voice2VoiceStrategyConfigDraft | null>(() => {
    if (strategy !== Strategy.VOICE2VOICE) {
      return null;
    }
    if (isVoiceConfig(strategyConfig)) {
      return strategyConfig;
    }
    return cloneStrategyConfig(DEFAULT_VOICE2VOICE_CONFIG);
  }, [strategy, strategyConfig]);

  const updateVoice2VoiceConfig = (
    mutator: (draft: Voice2VoiceStrategyConfigDraft) => void
  ) => {
    if (strategy !== Strategy.VOICE2VOICE) return;
    const base = voice2VoiceConfig
      ? cloneStrategyConfig(voice2VoiceConfig)
      : cloneStrategyConfig(DEFAULT_VOICE2VOICE_CONFIG);
    mutator(base);
    onStrategyConfigChange(base);
  };

  const resolveModelValue = (
    definition: ServiceProviderDefinition,
    settings: Record<string, any>
  ): string | undefined => {
    const modelField = definition.fields.find((field) => field.name === "model");
    if (!modelField) {
      return undefined;
    }
    const provided = settings.model ?? modelField.default;
    if (provided === undefined || provided === null || provided === "") {
      return undefined;
    }
    const candidate = String(provided);
    if (modelField.options?.length) {
      const allowed = new Set(modelField.options.map((option) => option.value));
      if (!allowed.has(candidate)) {
        return modelField.default !== undefined ? String(modelField.default) : undefined;
      }
    }
    return candidate;
  };

  const getEffectiveField = (
    field: ProviderFieldDefinition,
    modelValue: string | undefined
  ): ProviderFieldDefinition => {
    if (!modelValue || !field.model_overrides) {
      return field;
    }
    const modelOverride = field.model_overrides[modelValue];
    if (!modelOverride) {
      return field;
    }
    return {
      ...field,
      ...modelOverride,
    };
  };

  const isFieldVisibleForModel = (
    field: ProviderFieldDefinition,
    modelValue: string | undefined
  ): boolean => {
    if (!field.show_for_models?.length) {
      return true;
    }
    if (!modelValue) {
      return false;
    }
    return field.show_for_models.includes(modelValue);
  };

  const getEffectiveFields = (
    definition: ServiceProviderDefinition,
    settings: Record<string, any>
  ) => {
    const modelValue = resolveModelValue(definition, settings);
    const fields = definition.fields
      .map((field) => getEffectiveField(field, modelValue))
      .filter((field) => isFieldVisibleForModel(field, modelValue));
    return { fields, modelValue };
  };

  const sanitizeSettingsForDefinition = (
    definition: ServiceProviderDefinition,
    settings: Record<string, any>
  ): Record<string, any> => {
    const knownFields = new Set(definition.fields.map((field) => field.name));
    const sanitized: Record<string, any> = {};
    Object.entries(settings).forEach(([key, value]) => {
      if (!knownFields.has(key)) {
        sanitized[key] = value;
      }
    });

    const { fields } = getEffectiveFields(definition, settings);
    fields.forEach((field) => {
      const key = field.name;
      const providedValue = settings[key];
      let nextValue = providedValue;

      if (nextValue === undefined || nextValue === null || nextValue === "") {
        if (field.default !== undefined) {
          sanitized[key] = field.default;
        }
        return;
      }

      if (field.type === "select" && field.options?.length) {
        const allowed = new Set(field.options.map((option) => String(option.value)));
        if (!allowed.has(String(nextValue))) {
          if (field.default !== undefined) {
            sanitized[key] = field.default;
          }
          return;
        }
      }

      if (field.type === "number" || field.type === "range") {
        const numeric = typeof nextValue === "number" ? nextValue : Number(nextValue);
        const inRange =
          !Number.isNaN(numeric) &&
          (field.min === undefined || numeric >= field.min) &&
          (field.max === undefined || numeric <= field.max);
        if (!inRange) {
          if (field.default !== undefined) {
            sanitized[key] = field.default;
          }
          return;
        }
        nextValue = numeric;
      }

      if (field.type === "toggle") {
        nextValue = Boolean(nextValue);
      }

      sanitized[key] = nextValue;
    });

    return sanitized;
  };

  const buildDefaultSettings = (definition: ServiceProviderDefinition): Record<string, any> =>
    sanitizeSettingsForDefinition(definition, {});

  const handleProviderSelect = (service: ProviderServiceKey, providerId: string) => {
    const providerEnum = providerId ? (providerId as Provider) : null;
    const nextProviders: StrategyProviders = {
      ...strategyProviders,
      [service]: providerEnum,
    };
    onStrategyProvidersChange(nextProviders);

    const definition = manifest?.[service]?.find((item) => item.id === providerId);
    const settings = providerEnum
      ? definition
        ? buildDefaultSettings(definition)
        : {}
      : {};

    if (strategy === Strategy.STT_LLM_TTS) {
      updateSttConfig((draft) => {
        (draft as any)[service] = providerEnum
          ? {
              provider: providerEnum,
              settings,
            }
          : null;
      });
    } else if (strategy === Strategy.LLM_TTS && service !== "stt") {
      updateLlmTtsConfig((draft) => {
        (draft as any)[service] = providerEnum
          ? {
              provider: providerEnum,
              settings,
            }
          : null;
      });
    }
  };

  const handleFieldChange = (
    service: ProviderServiceKey,
    field: ProviderFieldDefinition,
    value: any
  ) => {
    const applyUpdate = (draft: ProviderSettingsDraft | null) => {
      const currentService = draft ?? {
        provider: strategyProviders[service],
        settings: {},
      };
      const definition = currentService.provider
        ? manifest?.[service]?.find((item) => item.id === currentService.provider)
        : undefined;
      const nextSettings = {
        ...currentService.settings,
        [field.name]: value,
      };
      currentService.settings = definition
        ? sanitizeSettingsForDefinition(definition, nextSettings)
        : nextSettings;
      return currentService;
    };

    if (strategy === Strategy.STT_LLM_TTS) {
      updateSttConfig((draft) => {
        (draft as any)[service] = applyUpdate((draft as any)[service] ?? null);
      });
    } else if (strategy === Strategy.LLM_TTS && service !== "stt") {
      updateLlmTtsConfig((draft) => {
        (draft as any)[service] = applyUpdate((draft as any)[service] ?? null);
      });
    }
  };

  const getServiceSelection = (service: ProviderServiceKey): ProviderSettingsDraft | null => {
    if (strategy === Strategy.LLM_TTS) {
      if (service === "stt") return null;
      return (llmTtsConfig as any)?.[service] ?? null;
    }
    return (sttConfig as any)?.[service] ?? null;
  };

  const renderField = (
    service: ProviderServiceKey,
    field: ProviderFieldDefinition,
    selected: ProviderSettingsDraft | null
  ) => {
    const value = selected?.settings?.[field.name] ?? field.default ?? "";

    if (field.type === "textarea") {
      return (
        <textarea
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          value={value}
          placeholder={field.placeholder}
          onChange={(e) => handleFieldChange(service, field, e.target.value)}
        />
      );
    }

    if (field.type === "select") {
      return (
        <select
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[rgb(255,204,51)] focus:ring-[rgb(255,204,51)] focus:ring-2 focus:outline-none"
          value={value}
          onChange={(e) => handleFieldChange(service, field, e.target.value)}
        >
          <option value="">Select</option>
          {field.options?.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === "toggle") {
      return (
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={Boolean(value)}
            onChange={(e) => handleFieldChange(service, field, e.target.checked)}
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[rgb(255,204,51)]/20 rounded-full peer peer-checked:bg-[rgb(255,204,51)] peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all" />
        </label>
      );
    }

    if (field.type === "number") {
      const numericKey = `${service}:${field.name}`;
      const rawNumberValue =
        numberDraftValues[numericKey] ??
        (value === undefined || value === null ? "" : String(value));
      return (
        <input
          type="number"
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          value={rawNumberValue}
          min={field.min}
          max={field.max}
          step={field.step}
          onChange={(e) => {
            const nextRawValue = e.target.value;
            setNumberDraftValues((prev) => ({
              ...prev,
              [numericKey]: nextRawValue,
            }));
          }}
          onBlur={() => {
            const raw = (
              numberDraftValues[numericKey] ??
              (value === undefined || value === null ? "" : String(value))
            ).trim();
            const fallback =
              field.default ??
              (typeof value === "number" ? value : undefined) ??
              field.min ??
              0;
            const parsed = raw === "" ? Number(fallback) : Number(raw);
            const committedValue = Number.isFinite(parsed)
              ? parsed
              : Number(fallback);
            handleFieldChange(service, field, committedValue);
            setNumberDraftValues((prev) => {
              const next = { ...prev };
              delete next[numericKey];
              return next;
            });
          }}
        />
      );
    }

    if (field.type === "range") {
      return (
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={field.min ?? 0}
            max={field.max ?? 1}
            step={field.step ?? 0.1}
            value={value}
            onChange={(e) => handleFieldChange(service, field, Number(e.target.value))}
            className="w-full"
          />
          <span className="text-sm text-gray-600 w-10 text-right">{Number(value).toFixed(2)}</span>
        </div>
      );
    }

    return (
      <input
        type="text"
        className="w-full px-3 py-2 border border-gray-300 rounded-md"
        value={value}
        placeholder={field.placeholder}
        onChange={(e) => handleFieldChange(service, field, e.target.value)}
      />
    );
  };

  const renderProviderRow = (service: ProviderServiceKey) => {
    const providers = manifest?.[service] ?? [];
    if (!providers.length) {
      return null;
    }
    const selectedProvider = strategyProviders[service];
    const definition = providers.find((item) => item.id === selectedProvider);
    const selection = getServiceSelection(service);
    const normalizedSettings = definition
      ? sanitizeSettingsForDefinition(definition, selection?.settings ?? {})
      : (selection?.settings ?? {});
    const normalizedSelection = selection
      ? {
          ...selection,
          settings: normalizedSettings,
        }
      : null;
    const effectiveFields = definition ? getEffectiveFields(definition, normalizedSettings).fields : [];

    return (
      <div key={service} className="bg-white rounded-lg border border-[#e7e2d3] p-4 space-y-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold text-[#140d0c]">{serviceLabels[service]}</h4>
            <p className="text-xs text-gray-500">Pick a provider to unlock advanced options.</p>
          </div>
          <select
            className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:border-[rgb(255,204,51)] focus:ring-[rgb(255,204,51)] focus:ring-2 focus:outline-none"
            value={selectedProvider ?? ""}
            onChange={(e) => handleProviderSelect(service, e.target.value)}
          >
            <option value="">Select provider</option>
            {providers.map((provider) => (
              <option key={provider.id} value={provider.id}>
                {provider.label}
              </option>
            ))}
          </select>
        </div>

        {selectedProvider && definition && (
          <div className="border border-dashed border-gray-300 rounded-md">
            <button
              type="button"
              className="w-full flex items-center justify-between px-3 py-2 text-left"
              onClick={() =>
                setExpandedPanels((prev) => ({
                  ...prev,
                  [service]: !prev[service],
                }))
              }
            >
              <span className="text-sm font-bold text-blue-600">Advanced Config ({definition.label})</span>
              <span>{expandedPanels[service] ? "−" : "+"}</span>
            </button>
            {expandedPanels[service] && (
              <div className="px-4 pb-4 space-y-4">
                {effectiveFields.length === 0 && (
                  <p className="text-sm text-gray-500">This provider has no configurable fields.</p>
                )}
                {effectiveFields.map((field) => (
                  <div key={field.name} className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">{field.label}</label>
                    {field.helpText && <p className="text-xs text-gray-500">{field.helpText}</p>}
                    {renderField(service, field, normalizedSelection)}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderStrategyRadios = () => (
    <div className="flex flex-wrap gap-4 mb-6">
      {[Strategy.VOICE2VOICE, Strategy.LLM_TTS, Strategy.STT_LLM_TTS].map((item) => (
        <label key={item} className="flex items-center space-x-2 cursor-pointer">
          <input
            type="radio"
            name="strategy"
            checked={strategy === item}
            onChange={() => onStrategyChange(item)}
            className="w-4 h-4 text-[rgb(255,204,51)] bg-gray-100 border-gray-300 focus:ring-[rgb(255,204,51)] focus:ring-2"
          />
          <span className="text-sm font-medium text-gray-900">
            {item === Strategy.VOICE2VOICE
              ? "Voice2Voice"
              : item === Strategy.LLM_TTS
              ? "LLM + TTS"
              : "STT + LLM + TTS"}
          </span>
        </label>
      ))}
    </div>
  );

  const renderVoice2VoiceOptions = () => {
    const selectedV2VProvider = strategyProviders.v2v;
    const v2vProviders = manifest?.v2v ?? [];
    const selectedV2VDefinition = v2vProviders.find((p) => p.id === selectedV2VProvider);

    // Get model options from manifest
    const modelField = selectedV2VDefinition?.fields.find((f) => f.name === "model");
    const modelOptions = modelField?.options ?? [];

    // Get voice options from manifest
    const voiceField = selectedV2VDefinition?.fields.find((f) => f.name === "voice");
    const voiceOptions = voiceField?.options ?? [];

    // Get temperature field config
    const temperatureField = selectedV2VDefinition?.fields.find((f) => f.name === "temperature");

    return (
      <div className="bg-white p-4 rounded-lg border border-[#e7e2d3] space-y-4">
        <div>
          <h4 className="text-sm font-medium text-[#6c6c6c]">Voice2Voice Provider</h4>
          <div className="flex flex-wrap gap-4 mt-3">
            {[Provider.VERTEXAI, Provider.OPENAI].map((provider) => (
              <label key={provider} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="voice2voice-provider"
                  checked={strategyProviders.v2v === provider}
                  onChange={() => {
                    onStrategyProvidersChange({
                      ...strategyProviders,
                      v2v: provider,
                    });
                    
                    // Update config with defaults from manifest when provider changes
                    const providerDef = v2vProviders.find((p) => p.id === provider);
                    if (providerDef) {
                      updateVoice2VoiceConfig((draft) => {
                        const modelField = providerDef.fields.find((f) => f.name === "model");
                        const tempField = providerDef.fields.find((f) => f.name === "temperature");
                        const voiceField = providerDef.fields.find((f) => f.name === "voice");
                        
                        if (modelField?.default) {
                          draft.model = modelField.default as string;
                        }
                        if (tempField?.default !== undefined) {
                          draft.temperature = tempField.default as number;
                        }
                        if (voiceField?.default) {
                          draft.voice_config = { voice: voiceField.default };
                        }
                      });
                    }
                  }}
                  className="w-4 h-4 text-[rgb(255,204,51)] bg-gray-100 border-gray-300 focus:ring-[rgb(255,204,51)] focus:ring-2"
                />
                <span className="text-sm font-medium text-gray-900">
                  {provider === Provider.VERTEXAI ? "VertexAI" : "OpenAI"}
                </span>
              </label>
            ))}
          </div>
        </div>

        {voice2VoiceConfig && selectedV2VProvider && (
          <div className="space-y-4">
            <label className="text-sm font-medium text-gray-700 block">
              Model
              <select
                value={voice2VoiceConfig.model}
                onChange={(e) =>
                  updateVoice2VoiceConfig((draft) => {
                    draft.model = e.target.value;
                  })
                }
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[rgb(255,204,51)] focus:ring-[rgb(255,204,51)] focus:ring-2 focus:outline-none"
              >
                {modelOptions.length === 0 && (
                  <option value={voice2VoiceConfig.model}>{voice2VoiceConfig.model}</option>
                )}
                {modelOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            {temperatureField && (
              <label className="text-sm font-medium text-gray-700 block">
                {temperatureField.label}
                <div className="flex items-center gap-3 mt-1">
                  <input
                    type="range"
                    min={temperatureField.min ?? 0}
                    max={temperatureField.max ?? 1}
                    step={temperatureField.step ?? 0.05}
                    value={voice2VoiceConfig.temperature ?? (typeof temperatureField.default === 'number' ? temperatureField.default : 0.7)}
                    onChange={(e) =>
                      updateVoice2VoiceConfig((draft) => {
                        draft.temperature = Number(e.target.value);
                      })
                    }
                    className="w-full"
                  />
                  <span className="text-sm text-gray-600 w-10 text-right">
                    {(voice2VoiceConfig.temperature ?? (typeof temperatureField.default === 'number' ? temperatureField.default : 0.7)).toFixed(2)}
                  </span>
                </div>
              </label>
            )}

            <label className="text-sm font-medium text-gray-700 block">
              Voice
              <select
                value={(voice2VoiceConfig.voice_config as Record<string, any> | null)?.voice || voiceField?.default || ""}
                onChange={(e) =>
                  updateVoice2VoiceConfig((draft) => {
                    const next = { ...(draft.voice_config ?? {}) } as Record<string, any>;
                    if (!e.target.value) {
                      delete next.voice;
                    } else {
                      next.voice = e.target.value;
                    }
                    draft.voice_config = Object.keys(next).length ? next : null;
                  })
                }
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[rgb(255,204,51)] focus:ring-[rgb(255,204,51)] focus:ring-2 focus:outline-none"
              >
                {voiceOptions.length === 0 && (
                  <option value="">Select voice</option>
                )}
                {voiceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
      </div>
    );
  };

  const renderServiceRows = () => (
    <div className="space-y-4">
      {activeServices.map((service) => renderProviderRow(service))}

      {strategy === Strategy.STT_LLM_TTS && sttConfig && (
        <div className="bg-white rounded-lg border border-[#e7e2d3] p-4">
          <label className="text-sm font-medium text-gray-700">
            User Idle Timeout (seconds)
            <input
              type="number"
              value={idleTimeoutInput}
              onChange={(e) =>
                setIdleTimeoutInput(e.target.value)
              }
              onBlur={() => {
                const raw = idleTimeoutInput.trim();
                const parsed = raw === "" ? 10 : Number(raw);
                const fallbackValue = Number.isFinite(parsed) ? parsed : 10;
                setIdleTimeoutInput(String(fallbackValue));
                updateSttConfig((draft) => {
                  draft.user_idle_timeout = fallbackValue;
                });
              }}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </label>
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-[#f2efe3] p-6 rounded-lg border border-[#e7e2d3] space-y-6">
      <div>
        <h3 className="text-lg font-medium">Select Strategy</h3>
        <p className="text-sm text-gray-600">Pick a pipeline and configure providers inline.</p>
      </div>

      {renderStrategyRadios()}

      {strategy === Strategy.VOICE2VOICE && renderVoice2VoiceOptions()}

      {strategy && strategy !== Strategy.VOICE2VOICE && manifest && renderServiceRows()}

      {!manifest && (
        <div className="bg-white p-4 rounded-lg border border-dashed border-[#e7e2d3] text-sm text-gray-700">
          Loading provider manifest...
        </div>
      )}
    </div>
  );
};

export default StrategySelector;
